import React, { FC, useCallback, useEffect, useState } from 'react';
import useAuth from '../../hooks/useAuthentication';
import { Divider, IconButton, List, ListItem, ListItemText, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SubHeading } from '../common/PageHeadings/PageHeadings';
import Card from '../common/Card/Card';
import { WarrantyRegistrationAWS } from '../../@types/AWS';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import SearchInput from '../common/SearchInput/SearchInput';
import { useNavigate } from 'react-router-dom';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import Button from '../common/Button';


interface DealerWarrantyRegistrationsProps { dealerId?: string }

const tableHeaders = ['owner', 'vehicle', 'serial', 'date registered', ''];

const DealerWarrantyRegistrations: FC<DealerWarrantyRegistrationsProps> = ({ dealerId }) => {

  const { user } = useAuth();

  const [searchValue, setSearchValue] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(true);

  const [warrantyRegistrations, setWarrantyRegistrations] = useState<WarrantyRegistrationAWS[]>([]);

  const [warrantyRegistrationsFiltered, setWarrantyRegistrationsFiltered] = useState<WarrantyRegistrationAWS[]>([]);

  const theme = useTheme();

  const navigate = useNavigate();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const fetchWarrantyRegistrations = useCallback(async () => {

    try {

      setLoading(true);

      const queryParams = { dealerId: dealerId ?? user?.companyAttributes?.dealerId };

      const response = await axios.get('/warranties/cart/registrations', { params: queryParams });

      setWarrantyRegistrations(response.data.data);

      setWarrantyRegistrationsFiltered(response.data.data);

    } catch (error: any) {

      console.log(error);

      toast.error('Error loading warranty registrations')

    } finally {

      setLoading(false);

    }

  }, [user, dealerId]);

  useEffect(() => { fetchWarrantyRegistrations() }, [fetchWarrantyRegistrations]);

  useEffect(() => {

    const filtered = warrantyRegistrations.filter(registration => {

      const matchingOwnerName = `${registration?.ownerInformation?.firstName} ${registration?.ownerInformation?.lastName}`.toLowerCase().includes(searchValue.toLowerCase());

      const matchingSerialNumber = `${registration?.vehicleInformation?.vehicleSerialNumber}`.toLowerCase().includes(searchValue.toLowerCase());

      const matchingVehicleModel = `${registration?.vehicleInformation?.vehicleYear} ${registration?.vehicleInformation?.vehicleModel}`.toLowerCase().includes(searchValue.toLowerCase());

      return matchingOwnerName || matchingSerialNumber || matchingVehicleModel;

    });

    setWarrantyRegistrationsFiltered(filtered);

  }, [searchValue, warrantyRegistrations, setWarrantyRegistrationsFiltered]);


  return (

    <Stack spacing={2}>

      <Stack direction="row" flexWrap="wrap" rowGap={2} columnGap={2} justifyContent={isTablet ? 'center' : 'space-between'} sx={{ marginBottom: '16px' }}>

        <SubHeading>Warranty Registrations</SubHeading>

        <SearchInput sx={{ width: isTablet ? '100%' : '375px' }} placeholder="name, serial, or vehicle year + model" setSearchValue={setSearchValue} />

      </Stack>

      <Card>

        {!loading ?

          (warrantyRegistrationsFiltered.length ?

            (!isTablet ?

              (

                <Stack direction="column" spacing={2} alignItems='flex-end'>

                  <TableContainer>

                    <Table>

                      <TableHead>

                        <TableRow>

                          {tableHeaders.map((header, index) => (

                            <TableCell key={index}
                              sx={{
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderTop: 'none',
                                textTransform: 'uppercase',
                                ...((header === 'date registered' || header === 'serial') && {
                                  textAlign: 'center'
                                }),
                              }}>

                              {header}

                            </TableCell>

                          ))}

                        </TableRow>

                      </TableHead>

                      <TableBody>

                        {warrantyRegistrationsFiltered.map((warrantyRegistration, index) => (

                          <TableRow key={index}
                            sx={{
                              '.MuiTableCell-root': {
                                border: 'none',
                                paddingTop: '12px',
                                paddingBottom: '12px'
                              },
                            }}>

                            <TableCell>

                              {warrantyRegistration?.ownerInformation?.company ?? `${warrantyRegistration?.ownerInformation?.firstName} ${warrantyRegistration?.ownerInformation?.lastName}`}

                            </TableCell>

                            <TableCell>

                              {warrantyRegistration?.vehicleInformation?.vehicleYear} {warrantyRegistration?.vehicleInformation?.vehicleModel}

                            </TableCell>

                            <TableCell sx={{ textAlign: 'center' }}>

                              {warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}

                            </TableCell>

                            <TableCell sx={{ textAlign: 'center' }}>

                              {moment(new Date(warrantyRegistration?.dateCreated ?? '')).format('MM/DD/YYYY')}

                            </TableCell>

                            <TableCell>

                              <Tooltip title="View Vehicle History" disableInteractive>

                                <IconButton onClick={() => { navigate(`/vehicle-history/${warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}`) }}>

                                  <FormatListBulletedOutlinedIcon />

                                </IconButton>

                              </Tooltip>

                            </TableCell>

                          </TableRow>

                        ))}

                      </TableBody>

                    </Table>

                  </TableContainer>

                </Stack>

              )


              : (

                <Stack direction="column" spacing={2} divider={<Divider />}>

                  {warrantyRegistrationsFiltered.map((warrantyRegistration, index) => (

                    <List key={index}
                      sx={{
                        '.MuiListItem-root': {
                          paddingLeft: 0,
                          paddingRight: 0,
                          '.MuiListItemText-root:last-of-type': {
                            textAlign: 'right',
                            maxWidth: '50%'
                          }
                        }
                      }}>

                      <ListItem>

                        <ListItemText>Owner</ListItemText>

                        <ListItemText>{warrantyRegistration?.ownerInformation?.company ?? `${warrantyRegistration?.ownerInformation?.firstName} ${warrantyRegistration?.ownerInformation?.lastName}`}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Vehicle</ListItemText>

                        <ListItemText>{warrantyRegistration?.vehicleInformation?.vehicleYear} {warrantyRegistration?.vehicleInformation?.vehicleModel}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Serial</ListItemText>

                        <ListItemText>{warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}</ListItemText>

                      </ListItem>

                      <ListItem>

                        <ListItemText>Date Registered</ListItemText>

                        <ListItemText>{moment(new Date(warrantyRegistration?.dateCreated ?? '')).format('MM/DD/YYYY')}</ListItemText>

                      </ListItem>

                      <Stack

                        direction="column"
                        
                        spacing={2}>

                        <Button onClick={() => { navigate(`/vehicle-history/${warrantyRegistration?.vehicleInformation?.vehicleSerialNumber}`) }}>

                          View Vehicle History

                        </Button>

                      </Stack>

                    </List>

                  ))}

                </Stack>

              )

            )

            : (searchValue
              ? (<Stack
                direction="column"
                justifyContent="center"
                alignItems="center"
                sx={{ minHeight: '150px' }}
                spacing={2}>

                <Typography>

                  Unable to locate any warranty registrations by registered name (first or last) serial number or model Type [{searchValue}]

                </Typography>

              </Stack>)
              : (
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ minHeight: '150px' }}
                  spacing={2}>

                  <Typography>

                    Unable to locate any warranty registrations Dealer ID# {dealerId ?? user?.companyAttributes?.dealerId}

                  </Typography>

                </Stack>
              )

            )

          )

          : (<WarrantyRegistrationsLoading />)

        }

      </Card>

    </Stack>

  );

}

const WarrantyRegistrationsLoading = () => {

  const theme = useTheme();

  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));

  const displayCount = 4;

  return (

    (!isTablet

      ? (

        <TableContainer>

          <Table>

            <TableHead>

              <TableRow>

                {tableHeaders.map((header, index) => (

                  <TableCell
                    key={index}
                    sx={{
                      borderLeft: 'none',
                      borderRight: 'none',
                      borderTop: 'none',
                      textTransform: 'uppercase',
                      ...((header === 'date created' || header === 'serial') && {
                        textAlign: 'center'
                      })
                    }}>

                    {header}

                  </TableCell>

                ))}

              </TableRow>

            </TableHead>

            <TableBody>

              {Array(displayCount).fill(null).map((value, index) => (

                <TableRow
                  key={index}
                  sx={{
                    '.MuiTableCell-root': {
                      border: 'none',
                      paddingTop: '12px',
                      paddingBottom: '12px'
                    }
                  }}>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                  <TableCell>

                    <Skeleton animation="wave" width={'100%'} height={20} />

                  </TableCell>

                </TableRow>

              ))}

            </TableBody>

          </Table>

        </TableContainer>

      )

      : (

        <Stack direction="column" spacing={2} divider={<Divider />}>

          {Array(displayCount).fill(null).map((value, index) => (

            <List key={index}
              sx={{
                '.MuiListItem-root': {
                  paddingLeft: 0,
                  paddingRight: 0,
                }
              }}>

              <ListItem>

                <ListItemText>Owner</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Vehicle</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Serial</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

              <ListItem>

                <ListItemText>Date Created</ListItemText>

                <Skeleton animation="wave" width={'50%'} height={20} />

              </ListItem>

            </List>

          ))}

        </Stack>

      )

    )

  )

}

export default DealerWarrantyRegistrations;
